import { Store } from "redux";
import { reportError } from "../../components/ticketing/basket-v2/errorReporting";
import { ADD_VOUCHER, REMOVE_VOUCHER } from "./store";
import { constants } from "../../components/ticketing/common";
import { postJsonWithResponse, deleteJsonWithResponse, EventEmitter, trackCheckoutEvent } from "../../utils";
import { evaluateResponse, removeSpaces, formatGiftCardVoucherCode } from "./utils";
import { events } from "../constants";

export class GiftVoucher {
  giftVoucher: HTMLElement;
  store: Store;
  applyButton: HTMLButtonElement;
  voucherInput: HTMLInputElement;
  appliedGiftcards: HTMLElement | null;
  checkoutPageId: HTMLInputElement | null;
  defaultError = { code: 400, message: "Error applying/removing a gift card" };

  constructor(selector: string, store: Store) {
    this.store = store;
    this.giftVoucher = document.querySelector(selector);
    this.appliedGiftcards = document.querySelector("#appliedGiftcards");
    this.checkoutPageId = document.querySelector("#checkoutPageId");

    if (!this.giftVoucher) return;

    this.applyButton = this.giftVoucher.querySelector<HTMLButtonElement>("[data-voucher-apply]");
    this.voucherInput = this.giftVoucher.querySelector<HTMLInputElement>("[data-voucher-input]");
    this.voucherInput.style.backgroundImage = "none";
    this.voucherInput.style.boxShadow = "none";
    this.bindEvents();
  }

  bindEvents() {
    this.voucherInput.addEventListener("input", () => {
      if (this.voucherInput.value == "") {
        this.voucherInput.classList.remove("is-invalid");
      }

      this.voucherInput.value = formatGiftCardVoucherCode(this.voucherInput.value);
    });
    this.applyButton.addEventListener("click", this.handleApplyGiftcard);
    this.giftVoucher.addEventListener("click", this.handleRemoveGiftcard);
  }

  handleApplyGiftcard = async () => {
    try {
      EventEmitter.emit(events.ORDER_UPDATE_START);

      const giftCard = removeSpaces(this.voucherInput.value);

      if (!giftCard || giftCard === "") {
        this.voucherInput.classList.add("is-invalid");
        EventEmitter.emit(events.ORDER_UPDATE_END);
        return;
      } else {
        this.voucherInput.classList.remove("is-invalid");
      }

      const giftCardRequest = {
        checkoutPageId: parseInt(this.checkoutPageId?.value ?? "0"),
        giftCard: giftCard,
      };

      const giftCardResponse = await postJsonWithResponse(constants.urls.basketV2.gift_card, giftCardRequest);
      const responseData = await evaluateResponse(
        giftCardResponse,
        this.voucherInput,
        this.defaultError.message,
        events.ORDER_UPDATE_END
      );

      if (responseData.status !== 200) {
        trackCheckoutEvent({
          message_name: responseData?.trackingMessageName ?? "voucher_error",
          message_text: responseData?.content ?? this.defaultError.message,
          message_type: "error",
          checkout_step: "checkout"
        });
        return;          
      }

      this.store.dispatch({
        type: ADD_VOUCHER,
        payload: {
          code: giftCard,
          amount: 0,
        },
      });

      this.appliedGiftcards.innerHTML = responseData.content;

      this.voucherInput.value = "";
      this.voucherInput.focus();
    } catch (e) {
      const message = "An error occurred applying the gift card";
      reportError(500, message);
      
      trackCheckoutEvent({
          message_name: "voucher_error",
          message_text: message,
          message_type: "error",
          checkout_step: "checkout"
      });
      
      EventEmitter.emit(events.ORDER_UPDATE_END);
    }
  };

  handleRemoveGiftcard = async (e: Event) => {
    try {
      const removeButton = e.target as HTMLElement;

      if ("discountRemove" in removeButton.dataset) {
        EventEmitter.emit(events.ORDER_UPDATE_START);

        const code = removeButton.dataset.discountCode;

        const giftCardRequest = {
          checkoutPageId: parseInt(this.checkoutPageId?.value ?? "0"),
          giftCard: code,
        };

        const giftCardResponse = await deleteJsonWithResponse(constants.urls.basketV2.gift_card, giftCardRequest);
        const responseData = await evaluateResponse(
          giftCardResponse,
          this.voucherInput,
          this.defaultError.message,
          events.ORDER_UPDATE_END
        );

        if (responseData.status !== 200) {
          trackCheckoutEvent({
            message_name: responseData?.trackingMessageName ?? "voucher_error",
            message_text: responseData?.content ?? this.defaultError.message,
            message_type: "error",
            checkout_step: "checkout"
          });
          return;          
        }


        this.appliedGiftcards.innerHTML = responseData.content;

        this.store.dispatch({
          type: REMOVE_VOUCHER,
          payload: {
            code,
          },
        });

        this.voucherInput.value = "";
        this.voucherInput.focus();

        const { vouchers } = this.store.getState();
        if (vouchers && vouchers.length === 0) {
          this.giftVoucher.classList.remove("voucher-applied");
        }
      }
    } catch (e) {
      const message = "An error occurred removing the gift card";
      reportError(500, message);
      
      trackCheckoutEvent({
          message_name: "voucher_error",
          message_text: message,
          message_type: "error",
          checkout_step: "checkout"
      });
      EventEmitter.emit(events.ORDER_UPDATE_END);
    }
  };
}
